import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Dropdown, DropdownProps } from 'primereact/dropdown';
import {
  VirtualScrollerProps,
} from 'primereact/virtualscroller';
import { dropdownAccessibility } from '../Dropdown/dropdownAccessibility';
import './single-select-dropdown.scss';
import SphereAvatar from '../../molecules/sphere-avatar/sphere-avatar';
import { GlyphColor } from '../../../types/glyph';
import { createRoot, Root } from 'react-dom/client';
import { Tooltip } from 'primereact/tooltip';

export interface OptionType {
  color?: string;
  name: string;
  code: string;
  glyph?: string; // Optional glyph for the type icon
  glyphColor?: GlyphColor; // Optional color for the glyph
  imageId?: string; // Optional imageId for fetching images
  /** Center sphere info (from center_center perspective assignment) */
  center?: {
    id: string;
    name: string;
    glyph?: string;
    glyphColor?: GlyphColor;
    imageId?: string;
  };
}

export interface PrimeReactSingleSelectProps
  extends Omit<
    DropdownProps,
    | 'value'
    | 'options'
    | 'onChange'
    | 'optionLabel'
    | 'valueTemplate'
    | 'itemTemplate'
  > {
  value: OptionType | null;
  options: OptionType[];
  onChange: (e: { value: OptionType | null }) => void;
  placeholder?: string;
  optionLabel?: keyof OptionType;
  filter?: boolean;
  className?: string;
  valueTemplate?: (option: OptionType | null) => JSX.Element;
  itemTemplate?: (option: OptionType) => JSX.Element;
  disabled?: boolean;
  dropdownIcon?: DropdownProps['dropdownIcon'];
  virtualScrollerOptions?: VirtualScrollerProps;
  scrollHeight?: string;
  loading?: boolean;
  onNearbyModeToggled?: (e: { nearbyMode: boolean }) => void;
  // New prop: if false, no toggle button is added.
  showNearbyToggle?: boolean;
  /**
   * EAINT-893: Additional class appended to the portalled panel's
   * className. Use when a consumer needs to scope panel styling to
   * itself without affecting every other PrimeReactSingleSelect in
   * the app (PrimeReact portals the panel outside the caller's DOM,
   * so CSS scoping via a parent class can't reach it). The default
   * `single-select-dropdown` class stays on the panel unconditionally.
   */
  panelClassName?: string;
}

const PrimeReactSingleSelect: React.FC<PrimeReactSingleSelectProps> = (props) => {
  const {
    value,
    options,
    onChange,
    placeholder = 'Select an option',
    optionLabel = 'name',
    filter = false,
    className = '',
    valueTemplate,
    itemTemplate,
    disabled = false,
    dropdownIcon = 'pi pi-chevron-down', // Default to the standard dropdown icon
    virtualScrollerOptions,
    scrollHeight = '300px',
    loading = false,
    onNearbyModeToggled,
    showNearbyToggle = false, // default false
    onShow,
    panelClassName: consumerPanelClassName,
    ...rest
  } = props;

  const a11y = dropdownAccessibility({ ...rest, placeholder, disabled });
  const [nearbyMode, setNearbyMode] = useState(false);
  const [overlayShown, setOverlayShown] = useState(false);

  // Default templates
  const defaultItemTemplate = (option: OptionType) => {
    const safeCode = option.code.replace(/[^\w-]/g, '_');
    const itemId  = `dropdown-item-${safeCode}`;
    // Get the label value, ensuring it's a string (not the center object)
    const labelValue = optionLabel === 'name' ? option.name : (option[optionLabel] as string) || '';
    return (
      <>
        <div
          id={itemId}
          className="dropdown-item white-space-nowrap overflow-hidden text-overflow-ellipsis"
        >
          {/* Main sphere avatar and name */}
          {(option.glyph || option.imageId) && (
            <SphereAvatar sphereId={option.code} glyph={option.glyph} glyphColor={option.glyphColor} imageId={option.imageId} decorative />
          )}
          <span>{labelValue}</span>

          {/* Center sphere (if available) */}
          {option.center && (
            <span className="dropdown-item__center">
              <SphereAvatar
                sphereId={option.center.id}
                glyph={option.center.glyph}
                glyphColor={option.center.glyphColor}
                imageId={option.center.imageId}
                sphereName={option.center.name}
                decorative
              />
              <span className="dropdown-item__center-name">{option.center.name}</span>
            </span>
          )}
        </div>
        <Tooltip target={`#${itemId}`} content={labelValue} position="top" showDelay={1000} />
      </>
    );
  };

  const defaultValueTemplate = (option: OptionType | null) => {
    if (!option) {
      return <span className="placeholder-text">{placeholder}</span>;
    }
    return (
      <div className="dropdown-selected-item">
        {(option.glyph || option.imageId) && (
          <SphereAvatar sphereId={option.code} glyph={option.glyph} glyphColor={option.glyphColor} imageId={option.imageId} decorative />
        )}
        <span>{option.name}</span>
      </div>
    );
  };

  const toggleIcon = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setNearbyMode((prev) => !prev);
  }, []);

  useEffect(() => {
    if (onNearbyModeToggled) {
      onNearbyModeToggled({ nearbyMode });
    }
  }, [nearbyMode, onNearbyModeToggled]);

  // Only run the effect to render the toggle button if showNearbyToggle is true.
  const toggleButtonRootRef = useRef<Root | null>(null);
  useEffect(() => {
    if (!overlayShown || !showNearbyToggle) return;

    const searchContainer = document.querySelector('.p-dropdown-filter-container');
  
    if (searchContainer) {
      let toggleWrapper = searchContainer.querySelector('.toggle-button-container') as HTMLDivElement;
  
      if (!toggleWrapper) {
        toggleWrapper = document.createElement('div');
        toggleWrapper.className = 'toggle-button-container';
        toggleWrapper.addEventListener('mousedown', (e) => e.stopPropagation());
        searchContainer.appendChild(toggleWrapper);
      }
  
      if (!toggleButtonRootRef.current) {
        toggleButtonRootRef.current = createRoot(toggleWrapper);
      }
  
      toggleButtonRootRef.current.render(
        <button
          className={`toggle-icon ${nearbyMode ? 'nearby-mode-icon' : 'everywhere-mode-icon'}`}
          onClick={toggleIcon}
          onMouseDown={(e) => e.preventDefault()}
        />
      );
    }
  }, [overlayShown, nearbyMode, toggleIcon, showNearbyToggle]);
  
  // Build the panel's className list. Keeping this outside the JSX so the
  // conditional additions don't require nested template literals (Sonar
  // "brain-overload" — the pre-refactor form had a template literal
  // wrapping another).
  const panelClasses = [
    'single-select-dropdown',
    showNearbyToggle && 'nearby-toggle-enabled',
    consumerPanelClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Dropdown
      value={value}
      onChange={onChange}
      options={options}
      optionLabel={optionLabel as string}
      dataKey="code"
      placeholder={placeholder}
      filter={filter}
      valueTemplate={valueTemplate || defaultValueTemplate}
      itemTemplate={itemTemplate || defaultItemTemplate}
      className={`single-select-root w-full ${className}`}
      panelClassName={panelClasses}
      disabled={disabled}
      dropdownIcon={dropdownIcon}
      virtualScrollerOptions={virtualScrollerOptions}
      scrollHeight={scrollHeight}
      loading={loading}
      onShow={() => {
        setOverlayShown(true);
        onShow?.();
      }}
      {...rest}
      {...a11y.nameAttrs}
      ariaLabel={a11y.ariaLabel}
      ariaLabelledBy={a11y.ariaLabelledBy}
      pt={a11y.pt}
    />
  );
};

export default PrimeReactSingleSelect;